/**
 * useYieldSpreadCurve Hook
 *
 * Calculates per-strain yield distribution from historical harvest data.
 * Analyses harvest records to determine the typical yield curve across flush days.
 *
 * US-PE-006: Sales Timeline - Phase 1 Data Hooks
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { getDefaultYieldCurve } from '@/utils/timeline-utils'
import { createDate } from '@/utils/dateUtils'

/**
 * Minimum gap in days between harvests to consider them separate flushes
 * @constant {number}
 */
const FLUSH_GAP_THRESHOLD_DAYS = 2

/**
 * Minimum number of flushes required to calculate a statistically meaningful curve
 * @constant {number}
 */
const MIN_FLUSHES_FOR_CURVE = 3

/**
 * Hook to calculate yield spread curve for a specific strain
 * Analyses historical harvest data to determine the day-by-day yield distribution
 *
 * @param {string|null} strainId - Strain UUID (null returns default curve)
 * @returns {Object} {
 *   curve: Object - Map of day offset to percentage (0-1),
 *   isDefault: boolean - True if using default curve (no/insufficient history),
 *   flushCount: number - Number of flushes analysed,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function useYieldSpreadCurve(strainId) {
  const [curve, setCurve] = useState(getDefaultYieldCurve())
  const [isDefault, setIsDefault] = useState(true)
  const [flushCount, setFlushCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAndCalculateCurve = useCallback(async () => {
    // If no strain specified, return default curve
    if (!strainId) {
      setCurve(getDefaultYieldCurve())
      setIsDefault(true)
      setFlushCount(0)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch harvest records for this strain's production batches
      // Join through production_batches to get strain-specific harvests
      const { data: harvests, error: fetchError } = await supabase
        .from('harvest_records')
        .select(`
          id,
          production_batch_id,
          harvest_date,
          flush_number,
          weight_kg
        `)
        .order('production_batch_id')
        .order('harvest_date')

      if (fetchError) {
        throw fetchError
      }

      // Also fetch the production batches to filter by strain
      const { data: batches, error: batchError } = await supabase
        .from('production_batches')
        .select('id, strain_id')
        .eq('strain_id', strainId)

      if (batchError) {
        throw batchError
      }

      // Create a set of batch IDs for this strain
      const strainBatchIds = new Set(batches.map(b => b.id))

      // Filter harvests to only those for this strain
      const strainHarvests = harvests.filter(h => strainBatchIds.has(h.production_batch_id))

      if (strainHarvests.length === 0) {
        // No harvest history for this strain - use default
        setCurve(getDefaultYieldCurve())
        setIsDefault(true)
        setFlushCount(0)
        setLoading(false)
        return
      }

      // Group harvests by batch and flush
      const flushData = groupHarvestsByFlush(strainHarvests)

      if (flushData.length < MIN_FLUSHES_FOR_CURVE) {
        // Not enough data - use default
        setCurve(getDefaultYieldCurve())
        setIsDefault(true)
        setFlushCount(flushData.length)
        setLoading(false)
        return
      }

      // Calculate the average yield curve across all flushes
      const calculatedCurve = calculateAverageCurve(flushData)

      setCurve(calculatedCurve)
      setIsDefault(false)
      setFlushCount(flushData.length)
    } catch (err) {
      console.error('[useYieldSpreadCurve] Error:', err)
      setError(err.message)
      setCurve(getDefaultYieldCurve())
      setIsDefault(true)
      setFlushCount(0)
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchAndCalculateCurve()
  }, [fetchAndCalculateCurve])

  return {
    curve,
    isDefault,
    flushCount,
    loading,
    error,
    refetch: fetchAndCalculateCurve
  }
}

/**
 * Group harvests by batch and identify individual flushes
 * A flush is identified by gaps of FLUSH_GAP_THRESHOLD_DAYS or more between harvests
 *
 * @param {Array} harvests - Harvest records sorted by batch and date
 * @returns {Array} Array of flush objects with day distributions
 */
function groupHarvestsByFlush(harvests) {
  const flushes = []
  const batchGroups = new Map()

  // Group by batch
  harvests.forEach(h => {
    if (!batchGroups.has(h.production_batch_id)) {
      batchGroups.set(h.production_batch_id, [])
    }
    batchGroups.get(h.production_batch_id).push(h)
  })

  // For each batch, identify flushes and calculate day distributions
  batchGroups.forEach((batchHarvests) => {
    // Sort by date
    batchHarvests.sort((a, b) => a.harvest_date.localeCompare(b.harvest_date))

    let currentFlush = []
    let flushStartDate = null

    batchHarvests.forEach((harvest, index) => {
      const harvestDate = createDate(harvest.harvest_date)

      if (currentFlush.length === 0) {
        // Start of a new flush
        flushStartDate = harvestDate
        currentFlush.push(harvest)
      } else {
        // Check gap from previous harvest
        const prevHarvest = currentFlush[currentFlush.length - 1]
        const prevDate = createDate(prevHarvest.harvest_date)
        const gapDays = Math.floor((harvestDate - prevDate) / (1000 * 60 * 60 * 24))

        if (gapDays >= FLUSH_GAP_THRESHOLD_DAYS) {
          // Gap detected - process current flush and start new one
          if (currentFlush.length > 0) {
            const flushDistribution = calculateFlushDistribution(currentFlush, flushStartDate)
            if (flushDistribution) {
              flushes.push(flushDistribution)
            }
          }

          // Start new flush
          currentFlush = [harvest]
          flushStartDate = harvestDate
        } else {
          // Same flush
          currentFlush.push(harvest)
        }
      }

      // Process final flush if this is the last harvest
      if (index === batchHarvests.length - 1 && currentFlush.length > 0) {
        const flushDistribution = calculateFlushDistribution(currentFlush, flushStartDate)
        if (flushDistribution) {
          flushes.push(flushDistribution)
        }
      }
    })
  })

  return flushes
}

/**
 * Calculate the day-by-day distribution for a single flush
 *
 * @param {Array} flushHarvests - Harvests within a single flush
 * @param {Date} flushStartDate - First day of the flush
 * @returns {Object|null} Distribution object or null if invalid
 */
function calculateFlushDistribution(flushHarvests, flushStartDate) {
  const totalWeight = flushHarvests.reduce((sum, h) => sum + parseFloat(h.weight_kg), 0)

  if (totalWeight === 0) return null

  const distribution = {}

  flushHarvests.forEach(harvest => {
    const harvestDate = createDate(harvest.harvest_date)
    const dayOffset = Math.floor((harvestDate - flushStartDate) / (1000 * 60 * 60 * 24))

    // Only consider days 0-6 (first week of flush)
    if (dayOffset >= 0 && dayOffset <= 6) {
      distribution[dayOffset] = (distribution[dayOffset] || 0) + parseFloat(harvest.weight_kg)
    }
  })

  // Convert to percentages
  Object.keys(distribution).forEach(day => {
    distribution[day] = distribution[day] / totalWeight
  })

  return {
    distribution,
    totalWeight,
    harvestCount: flushHarvests.length
  }
}

/**
 * Calculate the average yield curve across multiple flushes
 *
 * @param {Array} flushes - Array of flush distribution objects
 * @returns {Object} Average curve with day offset to percentage mapping
 */
function calculateAverageCurve(flushes) {
  // Collect all day offsets across all flushes
  const dayTotals = {}
  const dayCounts = {}

  flushes.forEach(flush => {
    Object.entries(flush.distribution).forEach(([day, percentage]) => {
      dayTotals[day] = (dayTotals[day] || 0) + percentage
      dayCounts[day] = (dayCounts[day] || 0) + 1
    })
  })

  // Calculate averages
  const curve = {}
  let totalPercentage = 0

  Object.keys(dayTotals).forEach(day => {
    curve[day] = dayTotals[day] / dayCounts[day]
    totalPercentage += curve[day]
  })

  // Normalise to ensure percentages sum to 1
  if (totalPercentage > 0 && Math.abs(totalPercentage - 1) > 0.001) {
    Object.keys(curve).forEach(day => {
      curve[day] = curve[day] / totalPercentage
    })
  }

  // Ensure we have at least days 0-4 (fill gaps with 0)
  for (let i = 0; i <= 4; i++) {
    if (curve[i] === undefined) {
      curve[i] = 0
    }
  }

  return curve
}

export default useYieldSpreadCurve
